/**
 * PriorityValidation Component
 * 
 * Displays existing priorities and shows warnings if the selected priority conflicts.
 * Allows override - warnings are informational, not blocking.
 */

import React from 'react';
import { useOfferPriorityValidation } from '@/app/hooks/useOfferPriorityValidation';
import './PriorityValidation.scss';

interface PriorityValidationProps {
  userId: string;
  partnerProductId: number;
  currentPriority: number;
  excludeOfferId?: string;
  onPriorityChange?: (priority: number) => void;
}

export const PriorityValidation: React.FC<PriorityValidationProps> = ({
  userId,
  partnerProductId,
  currentPriority,
  excludeOfferId,
  onPriorityChange,
}) => {
  const {
    offers,
    isLoading,
    error,
    getNextAvailablePriority,
    isPriorityConflicting,
    getConflictingOffers,
  } = useOfferPriorityValidation(userId, partnerProductId, excludeOfferId);

  if (isLoading) {
    return (
      <div className="priority-validation loading">
        <span className="spinner-small"></span> Checking priorities...
      </div>
    );
  }

  if (error) {
    return (
      <div className="priority-validation error">
        <span className="error-icon">⚠️</span> {error}
      </div>
    );
  }

  const isConflicting = isPriorityConflicting(currentPriority);
  const conflictingOffers = getConflictingOffers(currentPriority);
  const nextAvailable = getNextAvailablePriority();

  return (
    <div className="priority-validation">
      {/* Show conflict warning if priority is taken */}
      {isConflicting && conflictingOffers.length > 0 && (
        <div className="priority-conflict-warning">
          <span className="warning-icon">⚠️</span>
          <div className="warning-content">
            <strong>Priority {currentPriority} is already assigned</strong>
            <div className="conflict-details">
              {conflictingOffers.map(offer => (
                <div key={offer.offer_id} className="conflicting-offer">
                  {offer.reward_name || 'Unknown Reward'}
                </div>
              ))}
            </div>
            <p className="warning-message">
              Existing offer(s) will be shifted to priority {currentPriority + 1} when you save.
            </p>
          </div>
        </div>
      )}

      {/* Show existing priorities if there are any */}
      {offers.length > 0 && (
        <div className="existing-priorities">
          <div className="priorities-header">
            <span>Existing Priorities:</span>
            <button
              type="button"
              className="btn-suggest"
              onClick={() => onPriorityChange?.(nextAvailable)}
            >
              Use Next Available ({nextAvailable})
            </button>
          </div>
          <div className="priorities-list">
            {offers
              .sort((a, b) => a.display_priority - b.display_priority)
              .map(offer => (
                <div
                  key={offer.offer_id}
                  className={`priority-item ${offer.display_priority === currentPriority ? 'current' : ''}`}
                >
                  <span className="priority-number">{offer.display_priority}</span>
                  <span className="priority-reward">{offer.reward_name || 'Unknown Reward'}</span>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Show suggestion if no offers exist */}
      {offers.length === 0 && (
        <div className="no-priorities">
          <p>No existing offers for this user/partner combination.</p>
          <p className="suggestion">Suggested priority: {nextAvailable}</p>
        </div>
      )}
    </div>
  );
};

export default PriorityValidation;

